import { Button, cn } from "@win-reality/react-ui";
import { Minus, Plus } from "lucide-react";
import { CSSProperties, ReactNode } from "react";

/** Gray rounded panel holding one control. */
export const Card = ({
  className,
  style,
  children,
}: {
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}) => (
  <div
    className={cn(
      "rounded-[10px] bg-gray-100 p-3 dark:bg-white/[0.04]",
      className,
    )}
    style={style}
  >
    {children}
  </div>
);

export const SectionHeading = ({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) => (
  <div
    className={cn(
      "text-xs font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400",
      className,
    )}
  >
    {children}
  </div>
);

/** − value + */
export const PlusMinus = ({
  onDecrement,
  onIncrement,
  canDecrement = true,
  canIncrement = true,
  className = "gap-3",
  decrementTestId,
  incrementTestId,
  "data-team": team,
  children,
}: {
  onDecrement: () => void;
  onIncrement: () => void;
  canDecrement?: boolean;
  canIncrement?: boolean;
  className?: string;
  decrementTestId?: string;
  incrementTestId?: string;
  "data-team"?: "home" | "away";
  children: ReactNode;
}) => (
  <div className={cn("flex items-center", className)}>
    <Button
      variant="ghost"
      size="icon"
      onClick={onDecrement}
      isDisabled={!canDecrement}
      className="h-7 w-7 rounded-full bg-white text-gray-900 dark:bg-[#314158] dark:text-white"
      data-testid={decrementTestId}
      data-team={team}
    >
      <Minus className="h-4 w-4" />
    </Button>
    {children}
    <Button
      variant="ghost"
      size="icon"
      onClick={onIncrement}
      isDisabled={!canIncrement}
      className="h-7 w-7 rounded-full bg-white text-gray-900 hover:bg-gray-300 dark:bg-[#E2E8F0] dark:text-black dark:hover:bg-gray-200"
      data-testid={incrementTestId}
      data-team={team}
    >
      <Plus className="h-4 w-4" />
    </Button>
  </div>
);

export const CounterValue = ({
  value,
  "data-testid": testId,
  "data-team": team,
}: {
  value: number;
  "data-testid"?: string;
  "data-team"?: "home" | "away";
}) => (
  <span
    className="w-8 text-center font-semibold text-gray-900 dark:text-white"
    data-testid={testId}
    data-team={team}
    data-value={value}
  >
    {value}
  </span>
);

/** A team's score, edged in the team color. */
export const ScoreCard = ({
  label,
  color,
  value,
  onChange,
  valueTestId,
  incrementTestId,
  decrementTestId,
  "data-team": team,
  headingAction,
}: {
  label: string;
  color: string;
  value: number;
  onChange: (value: number) => void;
  valueTestId?: string;
  incrementTestId?: string;
  decrementTestId?: string;
  "data-team"?: "home" | "away";
  /** Beside the label, e.g. a link */
  headingAction?: ReactNode;
}) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between">
      <SectionHeading>{label}</SectionHeading>
      {headingAction}
    </div>
    <Card style={{ borderLeft: `2px solid ${color}` }}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-900 dark:text-white">
          Overall Score
        </span>
        <PlusMinus
          onDecrement={() => onChange(Math.max(0, value - 1))}
          onIncrement={() => onChange(value + 1)}
          decrementTestId={decrementTestId}
          incrementTestId={incrementTestId}
          data-team={team}
        >
          <CounterValue
            value={value}
            data-testid={valueTestId}
            data-team={team}
          />
        </PlusMinus>
      </div>
    </Card>
  </div>
);

/** Outs/balls/strikes: 0 to `max`, with a Clear link. */
export const CountCard = ({
  label,
  value,
  max,
  onChange,
  valueTestId,
  incrementTestId,
  decrementTestId,
  clearTestId,
}: {
  label: string;
  value: number;
  max: number;
  onChange: (value: number) => void;
  valueTestId?: string;
  incrementTestId?: string;
  decrementTestId?: string;
  clearTestId?: string;
}) => (
  <Card className="flex-1">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          {label}
        </span>
        <button
          onClick={() => onChange(0)}
          className="text-xs text-[#8F8FA6] underline hover:text-[#7A7A8E]"
          data-testid={clearTestId}
        >
          Clear
        </button>
      </div>
      <PlusMinus
        canDecrement={value > 0}
        canIncrement={value < max}
        onDecrement={() => onChange(Math.max(0, value - 1))}
        onIncrement={() => onChange(Math.min(max, value + 1))}
        decrementTestId={decrementTestId}
        incrementTestId={incrementTestId}
      >
        <CounterValue value={value} data-testid={valueTestId} />
      </PlusMinus>
    </div>
  </Card>
);
